import type { MockClient, MockEnvironment } from '@gotti/test-utils';
import { unique } from '../util/arrays.js';

const TRACKER = Symbol('gotti.playground.clientTracker');

type ClientListener = (client: MockClient, index: number) => void;

interface ClientTracker {
  /** The live clients, in creation order: appended as each is announced, pruned by `env.removeClient`. */
  known: MockClient[];
  listeners: ClientListener[];
}

/** Deduped, insertion-ordered — `env.clients` holds every client under two keys (private and public id). */
export const listClients = (env: MockEnvironment | null | undefined): MockClient[] =>
  unique(Object.values(env?.clients ?? {}).filter((client): client is MockClient => Boolean(client)));

const inputTokens = new WeakMap<MockClient, number>();
// Starts at 1, so a token is never the creation index of the same client on a fresh page: code that
// guesses a token from an index fails instead of passing by coincidence.
let nextInputToken = 1;

/**
 * The token a client's keyboard listeners are scoped under (test-utils' focus-scoped input): assigned on
 * the first ask, the client's for its whole life, never reused. Not `onClientCreated`'s `index`, which
 * restarts at 0 on every `createClients` call, nor a client's position, which shifts when one is removed.
 */
export const inputTokenOf = (client: MockClient): number => {
  let token = inputTokens.get(client);
  if (token === undefined) {
    token = nextInputToken++;
    inputTokens.set(client, token);
  }
  return token;
};

const trackerOf = (env: MockEnvironment): ClientTracker => {
  const existing = (env as unknown as Record<symbol, ClientTracker | undefined>)[TRACKER];
  if (existing) return existing;
  const tracker: ClientTracker = { known: [], listeners: [] };
  (env as unknown as Record<symbol, ClientTracker>)[TRACKER] = tracker;

  // The index is the client's slot in `known`, never its slot in `env.clients`: a client enters
  // `env.clients` only when it authenticates, so an `env.createClient` client would not be there yet.
  const announce = (client: MockClient | undefined) => {
    if (!client || tracker.known.includes(client)) return;
    const index = tracker.known.push(client) - 1;
    tracker.listeners.forEach((listener) => listener(client, index));
  };

  // One patch per environment: `createClients` calls the INTERNAL createClient, so both entry points
  // are wrapped and `known` dedupes a client that reaches us twice.
  const originalCreateClient = env.createClient.bind(env);
  env.createClient = ((...args: Parameters<MockEnvironment['createClient']>) => {
    const created = originalCreateClient(...args);
    announce(created);
    return created;
  }) as MockEnvironment['createClient'];
  const originalCreateClients = env.createClients.bind(env);
  env.createClients = (async (...args: Parameters<MockEnvironment['createClients']>) => {
    const created = await originalCreateClients(...args);
    created?.forEach(announce);
    return created;
  }) as MockEnvironment['createClients'];
  // The original deletes the client's `env.clients` key, so an id is resolved to its client first.
  const originalRemoveClient = env.removeClient.bind(env);
  env.removeClient = async (clientOrId: MockClient | string) => {
    const client: MockClient | undefined = typeof clientOrId === 'string' ? env.clients[clientOrId] : clientOrId;
    await originalRemoveClient(clientOrId);
    const at = client ? tracker.known.indexOf(client) : -1;
    if (at !== -1) tracker.known.splice(at, 1);
  };

  listClients(env).forEach(announce);
  return tracker;
};

/**
 * Calls `cb` once for every client the environment has now and once for every client it creates later,
 * and never for a client `env.removeClient` has removed.
 *
 * `index` is the client's position in creation order among the clients still alive: the replayed
 * clients get `0..n-1` and each later client the next one. It does not wait on authentication — an
 * `env.createClient` client is announced as it is created, a `createClients` batch as it resolves — so
 * it is never `-1`. Clients that already exist when the environment is first tracked are ordered as
 * `env.clients` lists them.
 */
export const forEachClientNowAndLater = (env: MockEnvironment, cb: ClientListener): void => {
  const tracker = trackerOf(env);
  tracker.listeners.push(cb);
  tracker.known.forEach((client, index) => cb(client, index));
};
